import { useCallback, useEffect, useMemo, useState } from 'react';
import { supabase } from '@/lib/supabase';
import { errorFingerprint, fetchErrorAlerts, isNoiseError, type PlatformErrorAlert } from '@/lib/platformLogs';
import { openErrorPage, prettyPage } from './logUtils';

// ─────────────────────────────────────────────────────────────
// Onglet « Santé de la plateforme » : vue mensuelle.
// Répond à une seule question : qu'est-ce qui casse le plus cette
// dernière semaine / ce dernier mois, et est-ce que ça s'améliore ?
// ─────────────────────────────────────────────────────────────

interface PlatformErrorLog {
  id: number;
  level: string;
  page: string | null;
  category: string | null;
  message: string;
  user_label: string | null;
  url: string | null;
  resolved: boolean;
  created_at: string;
}

interface MonthlyIncident {
  key: string;
  page: string;
  level: string;
  category: string | null;
  message: string;
  occurrences: number;
  lastAt: string;
  latest: PlatformErrorLog;
  open: boolean;
}

const DAY = 86400000;

function relative(iso: string): string {
  const min = Math.round((Date.now() - new Date(iso).getTime()) / 60000);
  if (min < 60) return `il y a ${Math.max(1, min)} min`;
  const hours = Math.round(min / 60);
  if (hours < 24) return `il y a ${hours} h`;
  return `il y a ${Math.round(hours / 24)} j`;
}

export default function PlatformHealthTab() {
  const [logs, setLogs] = useState<PlatformErrorLog[]>([]);
  const [alerts, setAlerts] = useState<PlatformErrorAlert[]>([]);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState<string | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    setLoadError(null);
    try {
      const since = new Date(Date.now() - 30 * DAY).toISOString();
      const { data, error } = await supabase
        .from('platform_error_logs')
        .select('id, level, page, category, message, user_label, url, resolved, created_at')
        .gte('created_at', since)
        .order('created_at', { ascending: false })
        .limit(3000);
      if (error) throw error;
      setLogs((data as PlatformErrorLog[]) || []);
      try {
        const rows = await fetchErrorAlerts(false, 200);
        setAlerts(rows);
      } catch {
        setAlerts([]);
      }
    } catch (err) {
      setLoadError(err instanceof Error ? err.message : 'Impossible de charger la santé de la plateforme');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  // 🧹 Les balises analytics / requêtes annulées (statut 0) ne sont pas des
  // pannes : on les retire de l'indice de santé et du Top 5.
  const visibleLogs = useMemo(() => logs.filter((l) => !isNoiseError(l.message)), [logs]);
  const noiseCount = logs.length - visibleLogs.length;

  const incidents = useMemo<MonthlyIncident[]>(() => {
    const map = new Map<string, MonthlyIncident>();
    visibleLogs.forEach((log) => {
      const key = errorFingerprint(log);
      const existing = map.get(key);
      if (!existing) {
        map.set(key, {
          key,
          page: log.page || '/',
          level: log.level,
          category: log.category,
          message: log.message,
          occurrences: 1,
          lastAt: log.created_at,
          latest: log,
          open: !log.resolved,
        });
        return;
      }
      existing.occurrences += 1;
      if (new Date(log.created_at).getTime() > new Date(existing.lastAt).getTime()) {
        existing.lastAt = log.created_at;
        existing.latest = log;
        existing.message = log.message;
      }
      if (!log.resolved) existing.open = true;
    });
    return Array.from(map.values());
  }, [logs]);

  const top5 = useMemo(
    () => [...incidents].sort((a, b) => b.occurrences - a.occurrences).slice(0, 5),
    [incidents],
  );
  const stats = useMemo(() => {
    const now = Date.now();
    const last7 = visibleLogs.filter((l) => now - new Date(l.created_at).getTime() < 7 * DAY).length;
    const prev7 = visibleLogs.filter((l) => {
      const diff = now - new Date(l.created_at).getTime();
      return diff >= 7 * DAY && diff < 14 * DAY;
    }).length;
    const trend = prev7 === 0 ? (last7 > 0 ? 100 : 0) : Math.round(((last7 - prev7) / prev7) * 100);
    const errors = visibleLogs.filter((l) => l.level === 'error').length;
    const pages = new Set(visibleLogs.map((l) => l.page || '/')).size;
    const openIncidents = incidents.filter((i) => i.open).length;
    const monthAlerts = alerts.filter((a) => now - new Date(a.created_at).getTime() < 30 * DAY);
    // Score simple : proportion d'incidents déjà traités + stabilité des 7 jours.
    const resolvedRatio = incidents.length === 0 ? 1 : (incidents.length - openIncidents) / incidents.length;
    const stability = trend <= 0 ? 1 : Math.max(0, 1 - Math.min(trend, 100) / 100);
    const score = Math.round(Math.min(100, (resolvedRatio * 0.6 + stability * 0.4) * 100));
    return {
      occurrences: visibleLogs.length,
      errors,
      incidents: incidents.length,
      pages,
      openIncidents,
      last7,
      prev7,
      trend,
      openAlerts: monthAlerts.filter((a) => !a.acknowledged).length,
      monthAlerts: monthAlerts.length,
      score,
    };
  }, [visibleLogs, incidents, alerts]);

  const totalTop = top5.reduce((sum, i) => sum + i.occurrences, 0) || 1;

  const scoreTone = stats.score >= 80
    ? { ring: 'text-accent-600', bg: 'bg-accent-100', text: 'text-accent-700', label: 'Bonne santé' }
    : stats.score >= 55
      ? { ring: 'text-secondary-600', bg: 'bg-secondary-100', text: 'text-secondary-800', label: 'À surveiller' }
      : { ring: 'text-red-500', bg: 'bg-red-50', text: 'text-red-600', label: 'Fragile' };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-20">
        <i className="ri-loader-4-line animate-spin text-2xl text-primary-500"></i>
      </div>
    );
  }

  if (loadError) {
    return (
      <div className="flex flex-col items-center justify-center py-16 border border-background-200/70 rounded-lg bg-background-50">
        <i className="ri-error-warning-line text-4xl text-red-400 mb-3"></i>
        <p className="text-sm text-foreground-600 mb-3">{loadError}</p>
        <button onClick={() => void load()} className="px-4 py-2 rounded-full bg-primary-500 text-background-50 text-xs font-medium cursor-pointer hover:bg-primary-600 transition-colors">
          Réessayer
        </button>
      </div>
    );
  }

  return (
    <div>
      {/* Bandeau de synthèse */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-3 md:gap-4 mb-6">
        <div className="bg-background-50 border border-background-200/70 rounded-lg p-5 flex items-center gap-4">
          <div className={`w-16 h-16 rounded-full ${scoreTone.bg} flex flex-col items-center justify-center flex-shrink-0`}>
            <span className={`text-lg font-bold font-heading ${scoreTone.ring}`}>{stats.score}</span>
            <span className={`text-[9px] font-medium ${scoreTone.ring}`}>/ 100</span>
          </div>
          <div className="min-w-0">
            <p className="text-sm font-semibold text-foreground-950">Indice de santé</p>
            <p className={`text-[11px] font-medium ${scoreTone.text} mb-1`}>{scoreTone.label}</p>
            <p className="text-[11px] text-foreground-500 leading-relaxed">
              Basé sur les incidents ouverts et l’évolution des erreurs sur 7 jours.
            </p>
          </div>
        </div>

        <div className="bg-background-50 border border-background-200/70 rounded-lg p-5">
          <p className="text-[11px] font-medium text-foreground-500 mb-1">Erreurs sur 30 jours</p>
          <p className="text-2xl font-bold font-heading text-foreground-950">{stats.occurrences.toLocaleString()}</p>
          <p className={`text-[11px] font-medium mt-1 flex items-center gap-1 ${stats.trend > 0 ? 'text-red-600' : 'text-accent-700'}`}>
            <i className={stats.trend > 0 ? 'ri-arrow-up-line' : 'ri-arrow-down-line'}></i>
            {stats.trend > 0 ? '+' : ''}{stats.trend}% vs semaine précédente
          </p>
          <p className="text-[11px] text-foreground-400 mt-1">7 derniers jours : {stats.last7} · 7 jours précédents : {stats.prev7}</p>
        </div>

        <div className="bg-background-50 border border-background-200/70 rounded-lg p-5">
          <p className="text-[11px] font-medium text-foreground-500 mb-1">Alertes du mois</p>
          <p className="text-2xl font-bold font-heading text-foreground-950">{stats.monthAlerts}</p>
          <p className="text-[11px] text-foreground-500 mt-1">
            dont <span className={stats.openAlerts > 0 ? 'text-red-600 font-semibold' : 'text-accent-700 font-semibold'}>{stats.openAlerts} encore ouvertes</span>
          </p>
          <p className="text-[11px] text-foreground-400 mt-1">{stats.pages} page(s) distincte(s) touchée(s)</p>
        </div>
      </div>

      {/* Top 5 des incidents récurrents */}
      <div className="rounded-lg border border-background-200/70 bg-background-50 p-4 md:p-5">
        <div className="flex flex-wrap items-center justify-between gap-2 mb-4">
          <div>
            <h3 className="text-sm font-semibold text-foreground-950 flex items-center gap-2">
              <i className="ri-fire-line text-accent-600"></i>
              Top 5 des incidents récurrents du mois
            </h3>
            <p className="text-[11px] text-foreground-500 mt-0.5">
              Les erreurs identiques sont regroupées : classement par nombre d’occurrences sur 30 jours.
              {noiseCount > 0 && (
                <span className="text-foreground-400"> · {noiseCount} bruit(s) technique(s) ignoré(s) (analytics, requêtes annulées)</span>
              )}
            </p>
          </div>
          <span className="text-[10px] px-2 py-1 rounded-full bg-secondary-100 text-secondary-900 font-medium whitespace-nowrap">
            {incidents.length} incident(s) distinct(s)
          </span>
        </div>

        {top5.length === 0 ? (
          <div className="text-center py-12">
            <i className="ri-shield-check-line text-4xl text-accent-400 mb-3 block"></i>
            <p className="text-sm font-medium text-foreground-700">Aucun incident ce mois-ci</p>
            <p className="text-xs text-foreground-500 mt-1">La plateforme est stable sur les 30 derniers jours.</p>
          </div>
        ) : (
          <div className="space-y-3">
            {top5.map((incident, index) => {
              const share = Math.round((incident.occurrences / totalTop) * 100);
              const isError = incident.level === 'error';
              return (
                <div key={incident.key} className="rounded-lg border border-background-200/70 bg-background-100/30 p-3 md:p-4">
                  <div className="flex items-start gap-3">
                    <span className={`w-7 h-7 rounded-lg flex items-center justify-center flex-shrink-0 text-[11px] font-bold ${
                      index === 0 ? 'bg-red-100 text-red-700' : 'bg-background-200/70 text-foreground-700'
                    }`}>
                      {index + 1}
                    </span>
                    <div className="flex-1 min-w-0">
                      <div className="flex flex-wrap items-center gap-2 mb-1">
                        <span className="text-xs font-semibold text-foreground-950">{prettyPage(incident.page)}</span>
                        <span className={`text-[10px] px-1.5 py-0.5 rounded-full font-semibold whitespace-nowrap ${
                          isError ? 'bg-red-100 text-red-700' : 'bg-amber-50 text-amber-700'
                        }`}>
                          ×{incident.occurrences}
                        </span>
                        {incident.category && (
                          <span className="text-[10px] px-1.5 py-0.5 rounded-full bg-secondary-100 text-secondary-800 font-medium whitespace-nowrap">
                            {incident.category}
                          </span>
                        )}
                        {incident.open ? (
                          <span className="text-[10px] px-1.5 py-0.5 rounded-full bg-primary-100 text-primary-700 font-medium whitespace-nowrap">ouvert</span>
                        ) : (
                          <span className="text-[10px] px-1.5 py-0.5 rounded-full bg-accent-100 text-accent-700 font-medium whitespace-nowrap">traité</span>
                        )}
                        <span className="text-[10px] text-foreground-400 whitespace-nowrap">{relative(incident.lastAt)}</span>
                      </div>
                      <p className="text-xs text-foreground-800 break-words line-clamp-2">{incident.message}</p>

                      <div className="mt-2 flex items-center gap-2">
                        <div className="flex-1 h-1.5 rounded-full bg-background-200/70 overflow-hidden min-w-0">
                          <div
                            className={`h-full rounded-full ${isError ? 'bg-red-400' : 'bg-amber-400'}`}
                            style={{ width: `${Math.max(4, share)}%` }}
                          ></div>
                        </div>
                        <span className="text-[10px] text-foreground-500 whitespace-nowrap">{share}% du top 5</span>
                      </div>
                    </div>
                    <button
                      onClick={() => openErrorPage(incident.latest)}
                      className="flex items-center gap-1.5 h-8 px-2.5 rounded-full border border-background-200/70 bg-background-50 text-[11px] font-medium text-foreground-700 hover:bg-primary-50 hover:border-primary-200 hover:text-primary-800 transition-colors cursor-pointer whitespace-nowrap flex-shrink-0"
                      title="Ouvrir la page en erreur"
                    >
                      <i className="ri-external-link-line"></i>
                      <span className="hidden md:inline">Ouvrir</span>
                    </button>
                  </div>
                </div>
              );
            })}
          </div>
        )}

        <p className="text-[10px] text-foreground-400 mt-4 leading-relaxed">
          Astuce : pour le détail complet d’un incident, ouvrez l’onglet « Erreurs plateforme » — vous y trouverez le contexte technique,
          l’URL exacte et le bouton d’export CSV à transmettre à un développeur.
        </p>
      </div>
    </div>
  );
}